import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { ConsentProvider, useConsent } from "@/contexts/ConsentContext";
import { FeatureFlagProvider } from "@/contexts/FeatureFlagContext";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Preferences from "./pages/Preferences";
import PreferencesRequest from "./pages/PreferencesRequest";
import PreferencesWithId from "./pages/PreferencesWithId";
import Subscribe from "./pages/Subscribe";
import Unsubscribe from "./pages/Unsubscribe";
import ValidateMagicLink from "./pages/ValidateMagicLink";
import EmailLinkTracker from "./pages/EmailLinkTracker";
import Feedback from "./pages/Feedback";
import ComebackPage from "./pages/ComebackPage";
import { CookieBanner } from "./components/CookieBanner";
import { CookiePolicy } from "./pages/CookiePolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import { TermsOfUse } from "./pages/TermsOfUse";
import SEOHead from "./components/SEOHead";
import StructuredData from "./components/StructuredData";
import ErrorBoundary from "./components/ErrorBoundary";

// Funnel pages
import InterestsPage from "./pages/funnel/InterestsPage";
import PlansPage from "./pages/funnel/PlansPage";
import FeedPreviewPage from "./pages/funnel/FeedPreviewPage";
import CheckoutPage from "./pages/funnel/CheckoutPage";
import WelcomePage from "./pages/funnel/WelcomePage";
import { AcquisitionRedirect } from "./components/AcquisitionRedirect";

// Conditional Analytics Components (lazy loaded)
const GoogleAnalytics = React.lazy(() => import("./components/GoogleAnalytics"));
const GoogleTagManager = React.lazy(() => import("./components/GoogleTagManager"));
const Hotjar = React.lazy(() => import("./components/Hotjar"));
const ConditionalMixpanel = React.lazy(() => import("./components/ConditionalMixpanel"));


const queryClient = new QueryClient();
const SentryRoutes = Sentry.withSentryRouting(Routes);

// Conditional Analytics Component
const ConditionalAnalytics = () => {
  const { hasAnalyticsConsent } = useConsent();
  if (!hasAnalyticsConsent) return null;
  return (
    <Suspense fallback={null}>
      <GoogleAnalytics />
      <GoogleTagManager />
      <Hotjar />
      <ConditionalMixpanel />
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ErrorBoundary>
        <FeatureFlagProvider>
          <ConsentProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
            <SEOHead />
            <ConditionalAnalytics />
            <StructuredData />
            <SentryRoutes>
              <Route path="/" element={<Index />} />
              <Route path="/subscribe" element={<AcquisitionRedirect />} />
              <Route path="/preferences/request" element={<PreferencesRequest />} />
              <Route path="/preferences/:subscriptionId" element={<PreferencesWithId />} />
              <Route path="/unsubscribe/:subscriptionId" element={<Unsubscribe />} />
              <Route path="/validate-magic-link" element={<ValidateMagicLink />} />
              <Route path="/link/:token" element={<EmailLinkTracker />} />
              <Route path="/preferences" element={<Preferences />} />
              <Route path="/feedback/:subscriptionId" element={<Feedback />} />
              <Route path="/comeback" element={<ComebackPage />} />
              
              {/* Funnel pages - 3-step funnel (v3 with feed preview) */}
              <Route path="/interests" element={<AcquisitionRedirect />} />
              <Route path="/plans" element={<AcquisitionRedirect />} />
              <Route path="/feed-preview" element={<AcquisitionRedirect />} />
              <Route path="/checkout" element={<AcquisitionRedirect />} />
              <Route path="/welcome" element={<AcquisitionRedirect />} />
              
              {/* Legal pages */}
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </SentryRoutes>
            <CookieBanner />
          </BrowserRouter>
          </ConsentProvider>
        </FeatureFlagProvider>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
